import React, {useEffect, useState} from "react";
import {
    Upload,
    Button,
    Table,
    message,
    Space,
    Modal,
    DatePicker,
    Card,
} from "antd";
import {
    UploadOutlined,
    FilePdfOutlined,
} from "@ant-design/icons";
import {
    Viewer,
    Worker,
} from '@react-pdf-viewer/core';
import {defaultLayoutPlugin} from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import dayjs, {Dayjs} from "dayjs";
import api from "../../api";
import type {ColumnType} from "antd/es/table";
import Title from "antd/es/typography/Title";

interface SettlementReceipt {
    id: number;
    month: string;
    uploaded_at: string;
    file: string;
    file_size?: number;
}

const SettlementsPage: React.FC = () => {
    const [month, setMonth] = useState<Dayjs | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [receipts, setReceipts] = useState<SettlementReceipt[]>([]);
    const [loading, setLoading] = useState(false);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const defaultLayoutPluginInstance = defaultLayoutPlugin();
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [prevObjectUrl, setPrevObjectUrl] = useState<string | null>(null)
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });


    useEffect(() => {
        fetchReceipts();
    }, []);

    const fetchReceipts = async () => {
        setLoading(true)
        try {
            const res = await api.get("/cc/settlement-receipts/");
            setReceipts(res.data.results);
        } catch (err) {
            console.error("Failed to fetch receipts", err);
            message.error("Failed to fetch receipts");
        } finally {
            setLoading(false)
        }
    };

    const fetchAndPreview = async (fileUrl: string) => {
        try {
            const res = await fetch(fileUrl, {credentials: "include"});
            if (!res.ok) throw new Error("Fetch failed");

            const blob = await res.blob();
            const newUrl = URL.createObjectURL(blob);

            if (prevObjectUrl) URL.revokeObjectURL(prevObjectUrl); // Clean up old blob
            setPrevObjectUrl(newUrl);
            setPreviewUrl(newUrl);
        } catch (err) {
            console.error(err);
            message.error("Failed to preview file.");
        }
    };

    const handleUpload = async () => {
        if (!month || !file) {
            return message.error("Please select a month and a file.");
        }

        const exists = receipts.find(
            (r) => dayjs(r.month).format("YYYY-MM") === month.format("YYYY-MM")
        );

        if (exists) {
            return message.warning("A receipt for this month already exists.");
        }

        const formData = new FormData();
        formData.append("month", month.format("YYYY-MM-01"));
        formData.append("file", file);

        try {
            setLoading(true);
            await api.post("/cc/settlement-receipts/", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            message.success("File uploaded successfully");
            fetchReceipts();
            setMonth(null);
            setFile(null);
        } catch (err) {
            console.error(err);
            message.error("Upload failed");
        } finally {
            setLoading(false);
        }
    };

    const columns: ColumnType<SettlementReceipt>[] = [
        {
            title: "Month",
            dataIndex: "month",
            key: "month",
            render: (val: string) => dayjs(val).format("MMMM YYYY"),
        },
        {
            title: "Uploaded",
            dataIndex: "uploaded_at",
            key: "uploaded_at",
            render: (val: string) => dayjs(val).format("YYYY-MM-DD HH:mm"),
        },
        {
            title: "Preview",
            key: "preview",
            render: (_, record) => (
                <Button
                    icon={<FilePdfOutlined/>}
                    type="link"
                    onClick={() => fetchAndPreview(record.file)}
                >
                    Preview
                </Button>
            ),
        },
        {
            title: "Download",
            key: "download",
            render: (_, record) => (
                <a href={record.file} download target="_blank" rel="noopener noreferrer">
                    <Button type="link">Download</Button>
                </a>
            ),
        },
        {
            title: "Size",
            dataIndex: "file_size",
            key: "file_size",
            render: (val: number) =>
                val ? `${(val / 1024).toFixed(1)} KB` : "N/A",
        },
    ];

    if (!user?.is_staff) {
        return (
            <div style={{padding: 20}}>
                You do not have access to this page.
            </div>
        );
    }

    return (
        <Card
            title={
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8
                }}>
                    <Title level={2} style={{margin: 0}}>Monthly Settlement Receipts</Title>
                </div>
            }
            style={{padding: 24}}
        >
            <Space
                direction="vertical"
                size="middle"
                style={{marginBottom: 24, width: "100%"}}
            >
                <DatePicker
                    picker="month"
                    value={month}
                    onChange={setMonth}
                    style={{width: 200}}
                    placeholder="Select month"
                />

                <Upload
                    accept=".pdf"
                    beforeUpload={(file) => {
                        setFile(file as File);
                        return false;
                    }}
                    fileList={file ? [file as any] : []}
                    onRemove={() => {
                        setFile(null);
                        return true;
                    }}
                >
                    <Button icon={<UploadOutlined/>}>Select PDF</Button>
                </Upload>

                <Button
                    type="primary"
                    loading={loading}
                    onClick={handleUpload}
                    disabled={!month || !file}
                >
                    Upload
                </Button>
            </Space>

            <Table
                scroll={{x: "max-content"}}
                loading={loading}
                dataSource={receipts}
                columns={columns}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                rowKey="id"
            />

            <Modal
                open={!!previewUrl}
                onCancel={() => setPreviewUrl(null)}
                footer={null}
                width={900}
                styles={{body: {padding: 0}}}
            >
                {previewUrl && (
                    <div style={{height: "80vh", overflow: "auto", padding: 16}}>
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                            <Viewer plugins={[defaultLayoutPluginInstance]} fileUrl={previewUrl}/>
                        </Worker>
                    </div>
                )}
            </Modal>
        </Card>
    );
};

export default SettlementsPage;
